/**
 * NewsletterList — /admin/newsletters
 * Grid of all newsletters with create, edit, delete, status badge,
 * duplicate, status workflow, pagination, and template gallery.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../utils/toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Mail,
  Trash2,
  RefreshCw,
  Copy,
  LayoutTemplate,
  BookOpen,
  Send,
  X,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { UPLOAD_URL } from "../../api/api";
import { resolveAssetUrl } from "./lib/assetUrl";
import { confirmDialog } from "../../components/em/confirm";
import askPrompt from "../../components/em/askPrompt";
import {
  T,
  Page,
  PageHeader,
  Card,
  PrimaryButton,
  SecondaryButton,
  DarkButton,
  IconButton,
  SearchInput,
  Tabs,
  StatusBadge,
  EmptyState,
  Pagination,
} from "../../components/em/ui";

// ─── Status workflow labels (badge rendering uses shared StatusBadge) ──────────

const STATUS_LABELS = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

// ─── NewsletterCard ───────────────────────────────────────────────────────────

function NewsletterCard({ newsletter, onDelete, onDuplicate, onStatusChange, onSaveTemplate }) {
  const navigate = useNavigate();

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor(diff / 60000);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return "just now";
  };

  // Status action: what workflow button to show based on current status
  const nextAction = {
    draft: { label: "Publish", next: "published" },
    published: { label: "Archive", next: "archived" },
    archived: { label: "Revert to Draft", next: "draft" },
  }[newsletter.status];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      onClick={() => navigate(`/admin/newsletters/${newsletter.id}`)}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = T.shadowMd)}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = T.shadowSm)}
      style={{
        background: T.bgCard,
        borderRadius: T.rXl,
        padding: 0,
        border: `1px solid ${T.border}`,
        cursor: "pointer",
        boxShadow: T.shadowSm,
        transition: "box-shadow 0.2s",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Cover — thumbnail if available, gradient otherwise */}
      <div
        style={{
          height: 120,
          overflow: "hidden",
          background: T.gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {newsletter.cover_image ? (
          <img
            src={
              newsletter.cover_image.startsWith("http")
                ? resolveAssetUrl(newsletter.cover_image)
                : `${UPLOAD_URL}${newsletter.cover_image}`
            }
            alt={newsletter.title || "Newsletter thumbnail"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <Mail size={40} color="rgba(255,255,255,0.7)" />
        )}
      </div>

      <div
        style={{
          padding: 18,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
        className="newsletterPage"
      >
        {/* Title */}
        <h3
          style={{
            margin: "0 0 6px",
            font: `600 15px ${T.font}`,
            color: T.fg1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {newsletter.title || "Untitled"}
        </h3>

        {newsletter.description && (
          <p
            style={{
              margin: "0 0 10px",
              font: `400 12px/1.5 ${T.font}`,
              color: T.fg5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {newsletter.description}
          </p>
        )}

        {/* Status row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: "auto",
            paddingTop: 8,
          }}
        >
          <StatusBadge label={STATUS_LABELS[newsletter.status] || "Draft"} />
          <span
            style={{
              flex: 1,
              font: `400 11px ${T.font}`,
              color: T.fg7,
              textAlign: "right",
            }}
          >
            {timeAgo(newsletter.updated_at)}
          </span>
        </div>

        {/* Action buttons row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px solid ${T.divider}`,
          }}
        >
          {nextAction && (
            <SecondaryButton
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(
                  newsletter.id,
                  newsletter.title,
                  nextAction.next,
                );
              }}
              style={{
                flex: 1,
                justifyContent: "center",
                padding: "6px 0",
                font: `600 12px/1 ${T.font}`,
              }}
            >
              {nextAction.label}
            </SecondaryButton>
          )}
          <IconButton
            icon={Copy}
            title="Duplicate"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(newsletter.id);
            }}
          />
          <IconButton
            icon={LayoutTemplate}
            title="Save as template"
            onClick={(e) => {
              e.stopPropagation();
              onSaveTemplate(newsletter);
            }}
          />
          <IconButton
            icon={Trash2}
            tone="danger"
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(newsletter.id);
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}

// ─── SavedTemplateCard — org-wide templates saved from real newsletters ───────

function SavedTemplateCard({ template, onUse, onDelete, creating }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      style={{
        background: T.bgCard,
        borderRadius: T.rXl,
        overflow: "hidden",
        border: `1px solid ${T.border}`,
        boxShadow: T.shadowSm,
        transition: "box-shadow 0.2s",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = T.shadowMd)}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = T.shadowSm)}
    >
      {/* Cover — thumbnail from the source newsletter, gradient otherwise */}
      <div
        style={{
          height: 80,
          overflow: "hidden",
          background: T.gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {template.cover_image ? (
          <img
            src={
              template.cover_image.startsWith("http")
                ? resolveAssetUrl(template.cover_image)
                : `${UPLOAD_URL}${template.cover_image}`
            }
            alt={template.title || "Template thumbnail"}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <LayoutTemplate size={32} color="rgba(255,255,255,0.7)" />
        )}
      </div>

      <div style={{ padding: "16px 18px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3 style={{ margin: "0 0 6px", font: `700 15px ${T.font}`, color: T.fg1 }}>
          {template.title || "Untitled template"}
        </h3>
        {template.description && (
          <p style={{ margin: "0 0 14px", font: `400 12px/1.5 ${T.font}`, color: T.fg5, flex: 1 }}>
            {template.description}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: "auto" }}>
          <IconButton
            icon={Trash2}
            tone="danger"
            title="Delete template"
            onClick={(e) => { e.stopPropagation(); onDelete(template.id); }}
          />
          <span style={{ flex: 1 }} />
          <button
            onClick={() => onUse(template)}
            disabled={creating}
            style={{
              padding: "7px 14px",
              font: `600 12px/1 ${T.font}`,
              background: T.gradient,
              color: "#fff",
              border: "none",
              borderRadius: T.rMd,
              cursor: creating ? "not-allowed" : "pointer",
              opacity: creating ? 0.7 : 1,
            }}
          >
            {creating ? "Creating…" : "Use Template"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Publish Modal ────────────────────────────────────────────────────────────

function PublishModal({ title, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          background: T.bgCard,
          borderRadius: T.rXl,
          padding: "40px 36px",
          maxWidth: 440,
          width: "90%",
          textAlign: "center",
          boxShadow: T.shadowMd,
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: T.fg7,
            display: "flex",
          }}
        >
          <X size={16} />
        </button>

        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h2
          style={{
            margin: "0 0 10px",
            font: `700 22px ${T.font}`,
            color: T.fg1,
          }}
        >
          Newsletter Published!
        </h2>
        <p
          style={{
            color: T.fg5,
            marginBottom: 28,
            font: `400 14px/1.6 ${T.font}`,
          }}
        >
          <strong>"{title}"</strong> is now published and visible to your team.
          Ready to send it out to your subscribers?
        </p>

        <button
          onClick={() => {
            toast.info("Email sending integration coming soon!");
            onClose();
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: T.success,
            color: "#fff",
            border: "none",
            borderRadius: T.rLg,
            padding: "12px 28px",
            cursor: "pointer",
            font: `600 14px ${T.font}`,
            marginBottom: 10,
            width: "100%",
            justifyContent: "center",
          }}
        >
          <Send size={15} /> Send Newsletter
        </button>
        <button
          onClick={onClose}
          style={{
            display: "block",
            width: "100%",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: T.fg7,
            font: `400 13px ${T.font}`,
            padding: "8px 0",
          }}
        >
          Close
        </button>
      </motion.div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NewsletterList() {
  const navigate = useNavigate();

  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("newsletters");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Publish modal
  const [publishModal, setPublishModal] = useState(null); // { id, title }

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Saved (server-side) templates — shared org-wide
  const [savedTemplates, setSavedTemplates] = useState([]);

  const load = useCallback((p = 1) => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/newsletters?page=${p}&limit=12`, { headers })
      .then((r) => {
        setNewsletters(r.data.data || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.pages || 1);
        setPage(r.data.page || 1);
      })
      .catch(() => toast.error("Could not load newsletters"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const loadTemplates = useCallback(() => {
    axios
      .get(`${API_URL}/api/newsletters?templates=1&limit=50`, { headers })
      .then((r) => setSavedTemplates(r.data.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    load(1);
    loadTemplates();
  }, []); // eslint-disable-line

  // Create blank newsletter
  const create = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/newsletters`,
        { title: "Untitled Newsletter" },
        { headers },
      );
      navigate(`/admin/newsletters/${res.data.id}`);
    } catch {
      toast.error("Could not create newsletter");
    } finally {
      setCreating(false);
    }
  };

  // Delete
  const deleteNewsletter = async (id) => {
    if (!(await confirmDialog("Delete this newsletter? This cannot be undone.")))
      return;
    try {
      await axios.delete(`${API_URL}/api/newsletters/${id}`, { headers });
      setNewsletters((prev) => prev.filter((n) => n.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Newsletter deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Duplicate
  const duplicateNewsletter = async (id) => {
    try {
      await axios.post(
        `${API_URL}/api/newsletters/${id}/duplicate`,
        {},
        { headers },
      );
      toast.success("Newsletter duplicated");
      load(1); // reload to show the copy at top
    } catch {
      toast.error("Duplicate failed");
    }
  };

  // Save an existing newsletter as an org-wide template
  const saveAsTemplate = async (newsletter) => {
    const name = await askPrompt("Name this template", newsletter.title || "", {
      placeholder: "e.g. Monthly Newsletter",
      okLabel: "Save template",
      required: "Please enter a template name.",
    });
    if (name === null) return; // cancelled
    try {
      await axios.post(
        `${API_URL}/api/newsletters/${newsletter.id}/save-as-template`,
        { title: name },
        { headers },
      );
      toast.success("Saved as template — find it in the Templates tab");
      loadTemplates();
    } catch {
      toast.error("Could not save as template");
    }
  };

  // Create a newsletter from a saved template (duplicate resets the
  // template flag, so the copy is a regular draft).
  const useSavedTemplate = async (template) => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/newsletters/${template.id}/duplicate`,
        {},
        { headers },
      );
      navigate(`/admin/newsletters/${res.data.id}`);
    } catch {
      toast.error("Could not create newsletter from template");
      setCreating(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!(await confirmDialog("Delete this template? This cannot be undone."))) return;
    try {
      await axios.delete(`${API_URL}/api/newsletters/${id}`, { headers });
      setSavedTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Template deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Status change
  const changeStatus = async (id, title, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/newsletters/${id}`,
        { status: newStatus },
        { headers },
      );
      setNewsletters((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: newStatus } : n)),
      );
      if (newStatus === "published") {
        setPublishModal({ id, title });
      } else {
        const labels = { archived: "Archived", draft: "Reverted to draft" };
        toast.success(labels[newStatus] || "Status updated");
      }
    } catch {
      toast.error("Could not update status");
    }
  };

  const filtered = newsletters.filter(
    (n) => !search || n.title?.toLowerCase().includes(search.toLowerCase()),
  );

  const tabs = [
    { key: "newsletters", label: "Newsletters", icon: BookOpen },
    { key: "templates", label: "Templates", icon: LayoutTemplate },
  ];

  return (
    <div className="content">
      <Page>
        {/* Publish modal */}
        <AnimatePresence>
          {publishModal && (
            <PublishModal
              title={publishModal.title}
              onClose={() => setPublishModal(null)}
            />
          )}
        </AnimatePresence>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <PageHeader
          icon={Mail}
          title="Newsletters"
          subtitle={`${total} newsletter${total !== 1 ? "s" : ""}`}
          actions={
            <PrimaryButton icon={Plus} onClick={create} disabled={creating}>
              {creating ? "Creating…" : "New Newsletter"}
            </PrimaryButton>
          }
        />

        {/* ── Tabs + toolbar ────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 22,
          }}
        >
          <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
          <span style={{ flex: 1 }} />
          {activeTab === "newsletters" && (
            <>
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search newsletters…"
                style={{ width: 220 }}
              />
              <IconButton
                icon={RefreshCw}
                title="Refresh"
                onClick={() => load(page)}
              />
            </>
          )}
        </div>

        {/* ── Newsletters tab ───────────────────────────────────────────── */}
        {activeTab === "newsletters" && (
          <>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  color: T.fg5,
                  paddingTop: 64,
                  font: `400 13px ${T.font}`,
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
                <p>Loading…</p>
              </div>
            ) : filtered.length === 0 ? (
              <Card padding={0}>
                <EmptyState
                  icon={Mail}
                  title={
                    search
                      ? "No newsletters match your search."
                      : "No newsletters yet."
                  }
                  subtitle={
                    search
                      ? "Try a different title."
                      : "Create your first newsletter to get started."
                  }
                  action={
                    !search && (
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <PrimaryButton icon={Plus} onClick={create}>
                          Blank newsletter
                        </PrimaryButton>
                        <SecondaryButton
                          icon={LayoutTemplate}
                          onClick={() => setActiveTab("templates")}
                        >
                          Browse templates
                        </SecondaryButton>
                      </div>
                    )
                  }
                />
              </Card>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: 20,
                  }}
                >
                  <AnimatePresence>
                    {filtered.map((n) => (
                      <NewsletterCard
                        key={n.id}
                        newsletter={n}
                        onDelete={deleteNewsletter}
                        onDuplicate={duplicateNewsletter}
                        onStatusChange={changeStatus}
                        onSaveTemplate={saveAsTemplate}
                      />
                    ))}
                  </AnimatePresence>
                </div>

                {/* Pagination — em component drives server-side page loads */}
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  pageSize={12}
                  onPage={load}
                />
              </>
            )}
          </>
        )}

        {/* ── Templates tab ────────────────────────────────────────────── */}
        {activeTab === "templates" && (
          <div>
            <p
              style={{
                margin: "0 0 20px",
                font: `400 14px ${T.font}`,
                color: T.fg5,
              }}
            >
              Choose a starting point — you can customise everything in the
              editor.
            </p>

            {/* Saved templates (created via "Save as template") */}
            {savedTemplates.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 20,
                }}
              >
                <AnimatePresence>
                  {savedTemplates.map((t) => (
                    <SavedTemplateCard
                      key={t.id}
                      template={t}
                      onUse={useSavedTemplate}
                      onDelete={deleteTemplate}
                      creating={creating}
                    />
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <Card padding={0}>
                <EmptyState
                  icon={LayoutTemplate}
                  title="No templates yet."
                  subtitle='Open a newsletter and choose "Save as template" to reuse its layout here.'
                />
              </Card>
            )}

            {/* Blank option */}
            <div
              style={{
                marginTop: 32,
                padding: "20px 24px",
                border: `2px dashed ${T.border}`,
                borderRadius: T.rXl,
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <div style={{ fontSize: 32 }}>📄</div>
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    margin: "0 0 4px",
                    font: `600 15px ${T.font}`,
                    color: T.fg1,
                  }}
                >
                  Start from scratch
                </p>
                <p
                  style={{
                    margin: 0,
                    color: T.fg5,
                    font: `400 13px ${T.font}`,
                  }}
                >
                  Blank canvas — build your own layout from the ground up.
                </p>
              </div>
              <DarkButton icon={Plus} onClick={create} disabled={creating}>
                Blank
              </DarkButton>
            </div>
          </div>
        )}
      </Page>
    </div>
  );
}
